import type { ReactNode } from 'react';
import type React from 'react';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { Form, FormControl } from 'react-bootstrap';
import { toast } from 'react-toastify';
import type { FormikHelpers } from 'formik/dist/types';
import SettingStyles from '../SettingStyle.module.scss';
import { useSessions } from '~/hooks/vendors/useSessions';
import type { VendorUserUpdatePasswordParam } from '~/models/VendorUser';
import { vendorUserPasswordValidationSchema } from '~/models/VendorUser';
import { FormGroup } from '~/components/common/parts/form/FormGroup';
import { Button } from '~/components/common/parts/button/Button';
import { changePassword } from '~/utils/api/vendors/VendorUser';
import { isSuccess } from '~/utils/api/api_handler';

export const ProfileSettingPassword: React.FC = () => {
  const { data: session, mutate: mutateSession } = useSessions().index();
  const handleSave = async (
    passwords: VendorUserUpdatePasswordParam,
    { resetForm }: FormikHelpers<VendorUserUpdatePasswordParam>
  ): Promise<void> => {
    const result = await changePassword(passwords);
    if (isSuccess(result)) {
      resetForm();
      await mutateSession?.();
      toast.success('パスワードを変更しました');
      return;
    }
    toast.error(result.message);
  };
  return (
    <div>
      <h4 className={SettingStyles.SettingSubTitle}>パスワードの変更</h4>
      <Formik
        initialValues={{
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        }}
        onSubmit={handleSave}
        validationSchema={vendorUserPasswordValidationSchema}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
          touched
        }: FormikProps<VendorUserUpdatePasswordParam>): ReactNode => (
          <div className={SettingStyles.MiddleSizeBlock}>
            {session?.vendorUser?.hasPassword && (
              <FormGroup>
                <label className={SettingStyles.NoBackgroundLabel}>
                  現在のパスワード
                </label>
                <FormControl
                  isInvalid={touched.oldPassword && !!errors.oldPassword}
                  name="oldPassword"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="password"
                  value={values.oldPassword}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.oldPassword}
                </Form.Control.Feedback>
              </FormGroup>
            )}
            <FormGroup>
              <label className={SettingStyles.NoBackgroundLabel}>
                新しいパスワード
              </label>
              <FormControl
                isInvalid={touched.newPassword && !!errors.newPassword}
                name="newPassword"
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                value={values.newPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.newPassword}
              </Form.Control.Feedback>
              <p className={SettingStyles.FormDescription}>
                空白にするとパスワードの入力を省略します
              </p>
            </FormGroup>
            <FormGroup>
              <label className={SettingStyles.NoBackgroundLabel}>
                新しいパスワード（確認用）
              </label>
              <FormControl
                isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                name="confirmPassword"
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                value={values.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword}
              </Form.Control.Feedback>
            </FormGroup>
            <Button
              className={SettingStyles.SettingSubmitButton}
              disabled={Object.keys(errors).length !== 0}
              onClick={handleSubmit}
              round
              variant="black_primary"
            >
              パスワードを変更
            </Button>
          </div>
        )}
      </Formik>
    </div>
  );
};
